import {
  PortalArticleBlock,
  PortalArticleCardItem,
  PortalArticleNoteStyle,
  PortalArticleSectionContentItem,
  PortalArticleTableItem,
  PortalArticleTopicBlock,
  PortalArtigoModel,
  PortalFaqAnswerBlock,
  PortalRichText,
  PortalRichTextPart,
} from '../../../models/PortalArtigoModel'
import {
  findDuplicatePortalAnchors,
  hasPortalAnchor,
  normalizePortalAnchorValue,
} from './anchor-utils'
import {
  PORTAL_BLOCK_TYPE_LABELS,
  PORTAL_HEX_COLOR_REGEX,
  PORTAL_NOTE_STYLE_COLOR_FIELDS,
  PORTAL_NOTE_STYLE_VARIANTS,
} from './portal-editor-options'
import { PORTAL_TABLE_MAX_COLUMNS, PORTAL_TABLE_MAX_ROWS } from './portal-table'
import { extractYoutubeVideoId } from './youtube-utils'

export type PortalArticleProblemSeverity = 'erro' | 'aviso'

export type PortalArticleProblemTargetType =
  | 'article'
  | 'coverImage'
  | 'thumb'
  | 'openGraph'
  | 'block'
  | 'faq'
  | 'cta'
  | 'courses'
  | 'relatedArticles'

export interface PortalArticleProblem {
  message: string
  severity: PortalArticleProblemSeverity
  targetType: PortalArticleProblemTargetType
  blockIndex?: number
  topicIndex?: number
  topicItemIndex?: number
  field?: string
  path?: string
}

export function validatePortalArtigoBeforeSave(artigo: PortalArtigoModel) {
  const problems: PortalArticleProblem[] = []

  if (!hasText(artigo.titulo)) {
    problems.push(createProblem('Informe o título do artigo.', 'article', 'titulo'))
  }
  if (!hasText(artigo.slug)) {
    problems.push(createProblem('Informe o slug do artigo.', 'article', 'slug'))
  }
  if (!hasText(artigo.resumo)) {
    problems.push(createProblem('Informe o resumo do artigo.', 'article', 'resumo'))
  }

  validateTipo(artigo, problems)

  if (hasText(artigo.imagemCapaUrl) && !hasText(artigo.imagemCapaAlt)) {
    problems.push(createProblem('Informe o texto alternativo da imagem de capa.', 'coverImage', 'imagemCapaAlt'))
  }
  if (hasPartialPair(artigo.imagemThumbUrl, artigo.imagemThumbAlt)) {
    problems.push(
      createProblem(
        'Thumb deve ter URL/path e texto alternativo, ou ambos devem ficar vazios.',
        'thumb',
        hasText(artigo.imagemThumbUrl) ? 'imagemThumbAlt' : 'imagemThumbUrl'
      )
    )
  }
  if (hasPartialPair(artigo.imagemOgUrl, artigo.imagemOgAlt)) {
    problems.push(
      createProblem(
        'Imagem Open Graph deve ter URL/path e texto alternativo, ou ambos devem ficar vazios.',
        'openGraph',
        hasText(artigo.imagemOgUrl) ? 'imagemOgAlt' : 'imagemOgUrl'
      )
    )
  }

  validateContentHierarchy(artigo, problems)

  validateDuplicateAnchors(artigo, problems)
  validateFaq(artigo, problems)
  if (artigo.tipo !== 'PILLAR') validateCta(artigo, problems)
  validateCourses(artigo, problems)
  validateRelatedArticles(artigo, problems)

  return problems
}

function validateTipo(artigo: PortalArtigoModel, problems: PortalArticleProblem[]) {
  if (artigo.tipo && artigo.tipo !== 'PILLAR') {
    problems.push(createProblem('Tipo de artigo inválido.', 'article', 'tipo', 'tipo'))
    return
  }

  if (artigo.tipo === 'PILLAR') validatePillarConfig(artigo, problems)
}

function validatePillarConfig(artigo: PortalArtigoModel, problems: PortalArticleProblem[]) {
  const sidebar = artigo.configuracaoPillar?.sidebar
  const indexCta = sidebar?.indexCta
  const banner = sidebar?.banner

  Object.values(sidebar?.indexLabels || {}).forEach((label) => {
    if (label.length > 35) {
      problems.push(createProblem('Pillar: os nomes do índice aceitam no máximo 35 caracteres.', 'article', 'configuracaoPillar.sidebar.indexLabels', 'configuracaoPillar.sidebar.indexLabels'))
    }
  })

  if (hasPartialPair(indexCta?.label, indexCta?.href)) {
    problems.push(
      createProblem(
        'Pillar: informe texto e URL do botão do índice, ou deixe ambos vazios.',
        'article',
        'configuracaoPillar.sidebar.indexCta',
        'configuracaoPillar.sidebar.indexCta'
      )
    )
  }
  if (hasText(indexCta?.href) && !isSafeNavigationHref(indexCta.href)) {
    problems.push(
      createProblem(
        'Pillar: a URL do botão do índice é inválida.',
        'article',
        'configuracaoPillar.sidebar.indexCta.href',
        'configuracaoPillar.sidebar.indexCta.href'
      )
    )
  }

  if (banner?.visible === false) return

  const hasLegacyBannerContent =
    hasText(banner?.title) ||
    hasText(banner?.label) ||
    hasText(banner?.href) ||
    (banner?.paragraphs || []).some(hasRichTextContent)

  const activeType = banner?.type || (hasLegacyBannerContent ? 'text' : undefined)
  if (!activeType) return

  if (activeType === 'image') {
    if (!hasText(banner?.image?.src)) {
      problems.push(createProblem('Pillar: informe a URL/path da imagem do banner.', 'article', 'configuracaoPillar.sidebar.banner.image.src', 'configuracaoPillar.sidebar.banner.image.src'))
    }
    if (!hasText(banner?.image?.alt)) {
      problems.push(createProblem('Pillar: informe o texto alternativo da imagem do banner.', 'article', 'configuracaoPillar.sidebar.banner.image.alt', 'configuracaoPillar.sidebar.banner.image.alt'))
    }
    if (!hasText(banner?.image?.href)) {
      problems.push(createProblem('Pillar: informe a URL de destino da imagem do banner.', 'article', 'configuracaoPillar.sidebar.banner.image.href', 'configuracaoPillar.sidebar.banner.image.href'))
    } else if (!isSafeNavigationHref(banner.image.href)) {
      problems.push(createProblem('Pillar: a URL de destino da imagem do banner é inválida.', 'article', 'configuracaoPillar.sidebar.banner.image.href', 'configuracaoPillar.sidebar.banner.image.href'))
    }
    return
  }

  if (!hasText(banner?.title)) {
    problems.push(createProblem('Pillar: informe o título do banner.', 'article', 'configuracaoPillar.sidebar.banner.title', 'configuracaoPillar.sidebar.banner.title'))
  }
  if (!hasText(banner?.label)) {
    problems.push(createProblem('Pillar: informe o texto do botão do banner.', 'article', 'configuracaoPillar.sidebar.banner.label', 'configuracaoPillar.sidebar.banner.label'))
  }
  if (!hasText(banner?.href)) {
    problems.push(createProblem('Pillar: informe a URL do botão do banner.', 'article', 'configuracaoPillar.sidebar.banner.href', 'configuracaoPillar.sidebar.banner.href'))
  } else if (!isSafeNavigationHref(banner.href)) {
    problems.push(createProblem('Pillar: a URL do botão do banner é inválida.', 'article', 'configuracaoPillar.sidebar.banner.href', 'configuracaoPillar.sidebar.banner.href'))
  }

  if (!(banner?.paragraphs || []).some(hasRichTextContent)) {
    problems.push(createProblem('Pillar: adicione ao menos um parágrafo ao banner.', 'article', 'configuracaoPillar.sidebar.banner.paragraphs', 'configuracaoPillar.sidebar.banner.paragraphs'))
  }

  ;(banner?.paragraphs || []).forEach((paragraph, index) => {
    validateRichText(paragraph, `Pillar: banner parágrafo ${index + 1}`, problems, {
      targetType: 'article',
      field: 'configuracaoPillar.sidebar.banner.paragraphs',
      path: `configuracaoPillar.sidebar.banner.paragraphs.${index}`,
    })
  })
}

function validateContentHierarchy(artigo: PortalArtigoModel, problems: PortalArticleProblem[]) {
  const blocks = artigo.conteudo || []
  const isPublished = artigo.status === 'PUBLICADO'
  const introIndexes = blocks.flatMap((block, index) => (block.type === 'intro' ? [index] : []))
  const summaryIndexes = blocks.flatMap((block, index) => (block.type === 'summary' ? [index] : []))
  const topicIndexes = blocks.flatMap((block, index) => (block.type === 'topic' ? [index] : []))

  if (introIndexes.length > 1) problems.push(createProblem('O artigo deve possuir no máximo uma introdução.', 'article', 'conteudo', 'conteudo'))
  if (summaryIndexes.length > 1) problems.push(createProblem('O artigo deve possuir no máximo um índice.', 'article', 'conteudo', 'conteudo'))

  addRequiredStructureProblem(problems, isPublished, summaryIndexes.length === 1, 'Adicione o índice obrigatório.')
  addRequiredStructureProblem(problems, isPublished, topicIndexes.length > 0, 'Adicione ao menos um tópico com H2.')

  if (
    isPublished &&
    summaryIndexes.length === 1 &&
    topicIndexes.length > 0 &&
    !(
      summaryIndexes[0] < topicIndexes[0] &&
      (introIndexes.length === 0 || introIndexes[0] < summaryIndexes[0])
    )
  ) {
    problems.push(createProblem('A ordem obrigatória é introdução opcional, índice e tópicos.', 'article', 'conteudo', 'conteudo'))
  }

  const topicIds = new Set<string>()
  const tableIds = new Set<string>()
  blocks.forEach((block, blockIndex) => {
    if (block.type === 'intro' || block.type === 'summary') {
      validateBlock(block, blockIndex, artigo, problems, tableIds)
      return
    }
    if (block.type !== 'topic') {
      const legacyType = (block as unknown as { type: string }).type
      problems.push(createBlockProblem(`Bloco raiz legado "${legacyType}" não é aceito.`, blockIndex, 'type', `conteudo.${blockIndex}.type`))
      return
    }

    if (!hasText(block.id)) {
      problems.push(createTopicProblem('Tópico sem ID técnico.', blockIndex, blockIndex, undefined, 'id'))
    } else if (topicIds.has(block.id)) {
      problems.push(createTopicProblem(`ID de tópico duplicado: "${block.id}".`, blockIndex, blockIndex, undefined, 'id'))
    } else topicIds.add(block.id)

    validateTopic(block, blockIndex, artigo, problems, tableIds)
  })
}

function addRequiredStructureProblem(
  problems: PortalArticleProblem[],
  isPublished: boolean,
  valid: boolean,
  message: string
) {
  if (valid) return
  problems.push({
    ...createProblem(message, 'article', 'conteudo', 'conteudo'),
    severity: isPublished ? 'erro' : 'aviso',
  })
}

function validateTopic(
  topic: PortalArticleTopicBlock,
  blockIndex: number,
  artigo: PortalArtigoModel,
  problems: PortalArticleProblem[],
  tableIds: Set<string>
) {
  const mainItems = topic.items.filter((item) => item.type === 'section' && item.role === 'main')
  const quickSummaries = topic.items.filter((item) => item.type === 'quickSummary')
  if (mainItems.length > 1) {
    problems.push(createTopicProblem('Tópico não pode possuir mais de um H2 principal.', blockIndex, blockIndex))
  }
  if (mainItems.length === 0) {
    const problem = createTopicProblem('Tópico deve possuir um H2 principal.', blockIndex, blockIndex)
    problem.severity = artigo.status === 'PUBLICADO' ? 'erro' : 'aviso'
    problems.push(problem)
  }
  if (quickSummaries.length > 1) {
    problems.push(createTopicProblem('Tópico não pode possuir mais de um resumo rápido.', blockIndex, blockIndex))
  }

  let mainFound = false
  topic.items.forEach((item, itemIndex) => {
    const basePath = `conteudo.${blockIndex}.items.${itemIndex}`
    if (item.type === 'image') {
      validateImageItem(item, blockIndex, blockIndex, itemIndex, basePath, problems)
      return
    }
    if (item.type === 'quickSummary') {
      if (!mainFound) {
        problems.push(createTopicProblem('Resumo rápido deve aparecer depois do H2.', blockIndex, blockIndex, itemIndex, 'quickSummary'))
      }
      if (!Array.isArray(item.quickSummary.items)) {
        problems.push(createTopicProblem('Resumo rápido deve possuir uma lista de itens.', blockIndex, blockIndex, itemIndex, 'quickSummary.items'))
      } else {
        validateRichText(item.quickSummary.paragraph, 'Resumo rápido: parágrafo', problems, {
          targetType: 'block', blockIndex, field: 'quickSummary.paragraph', path: `${basePath}.quickSummary.paragraph`,
        })
        item.quickSummary.items.forEach((value, index) => validateRichText(value, `Resumo rápido: item ${index + 1}`, problems, {
          targetType: 'block', blockIndex, field: 'quickSummary.items', path: `${basePath}.quickSummary.items.${index}`,
        }))
      }
      tagNewProblemsWithTopic(problems, blockIndex, itemIndex, basePath)
      return
    }

    if (item.role === 'main') mainFound = true
    else if (!mainFound) {
      problems.push(createTopicProblem('Subtópico deve aparecer depois do H2.', blockIndex, blockIndex, itemIndex, 'role'))
    }
    if (artigo.status === 'PUBLICADO' && !hasText(item.section.title)) {
      problems.push(createTopicProblem(`${item.role === 'main' ? 'H2' : 'H3'} deve possuir título.`, blockIndex, blockIndex, itemIndex, 'section.title'))
    }
    if (artigo.status === 'PUBLICADO' && !hasText(item.section.anchorId) && !hasText(item.section.id)) {
      problems.push(createTopicProblem(`${item.role === 'main' ? 'H2' : 'H3'} deve possuir âncora ou id.`, blockIndex, blockIndex, itemIndex, 'section.anchorId'))
    }
    const legacyFields = ['headingLevel', 'paragraphs', 'bullets', 'note'] as const
    legacyFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(item.section, field)) {
        problems.push(createTopicProblem(`Seção possui campo legado não aceito: ${field}.`, blockIndex, blockIndex, itemIndex, `section.${field}`))
      }
    })
    const start = problems.length
    validateSectionContent(
      item.section.content || [],
      item.role === 'main' ? 'H2' : 'H3',
      blockIndex,
      false,
      artigo.status === 'PUBLICADO',
      problems,
      tableIds
    )
    problems.slice(start).forEach((problem) => {
      problem.topicIndex = blockIndex
      problem.topicItemIndex = itemIndex
      problem.path = problem.path?.replace(`conteudo.${blockIndex}.section.content`, `${basePath}.section.content`)
    })
  })
}

function validateImageItem(
  item: Extract<PortalArticleSectionContentItem, { type: 'image' }>,
  blockIndex: number,
  topicIndex: number,
  topicItemIndex: number,
  path: string,
  problems: PortalArticleProblem[]
) {
  if (!hasText(item.image.src)) problems.push(createTopicProblem('Informe a URL/path da imagem.', blockIndex, topicIndex, topicItemIndex, 'image.src', `${path}.image.src`))
  if (!hasText(item.image.alt)) problems.push(createTopicProblem('Informe o texto alternativo da imagem.', blockIndex, topicIndex, topicItemIndex, 'image.alt', `${path}.image.alt`))
  if (!(item.image.width > 0) || !(item.image.height > 0)) problems.push(createTopicProblem('Informe largura e altura válidas para a imagem.', blockIndex, topicIndex, topicItemIndex, 'image.dimensions', `${path}.image`))
  if (!isValidOptionalImageHref(item.image.href)) problems.push(createTopicProblem('Informe um link http(s) ou caminho interno iniciado por /.', blockIndex, topicIndex, topicItemIndex, 'image.href', `${path}.image.href`))
}

function tagNewProblemsWithTopic(
  problems: PortalArticleProblem[],
  topicIndex: number,
  topicItemIndex: number,
  _basePath: string
) {
  const problem = problems[problems.length - 1]
  if (problem?.blockIndex === topicIndex) {
    problem.topicIndex = topicIndex
    problem.topicItemIndex = topicItemIndex
  }
}

function validateBlock(
  block: PortalArticleBlock,
  index: number,
  artigo: PortalArtigoModel,
  problems: PortalArticleProblem[],
  tableIds: Set<string>
) {
  const context = `Bloco ${index + 1} (${PORTAL_BLOCK_TYPE_LABELS[block.type]})`

  if (block.type === 'summary') {
    ;(block.topics || []).forEach((topic, topicIndex) => {
      const topicPath = `conteudo.${index}.topics.${topicIndex}`

      if (!hasText(topic.title)) {
        problems.push(createBlockProblem(`${context}: o tópico ${topicIndex + 1} está sem texto.`, index, 'summary.topic.title', topicPath))
      }
      if (!hasText(topic.href)) {
        problems.push(createBlockProblem(`${context}: o tópico ${topicIndex + 1} está sem link.`, index, 'summary.topic.href', topicPath))
      } else {
        validateSummaryHref(topic.href, `${context}: o tópico ${topicIndex + 1}`, index, `${topicPath}.href`, artigo, problems)
      }

      ;(topic.subtopics || []).forEach((subtopic, subtopicIndex) => {
        const subtopicPath = `${topicPath}.subtopics.${subtopicIndex}`

        if (!hasText(subtopic.title)) {
          problems.push(createBlockProblem(`${context}: o subtópico ${subtopicIndex + 1} está sem texto.`, index, 'summary.subtopic.title', subtopicPath))
        }
        if (!hasText(subtopic.href)) {
          problems.push(createBlockProblem(`${context}: o subtópico ${subtopicIndex + 1} está sem link.`, index, 'summary.subtopic.href', subtopicPath))
        } else {
          validateSummaryHref(subtopic.href, `${context}: o subtópico ${subtopicIndex + 1}`, index, `${subtopicPath}.href`, artigo, problems)
        }
      })
    })
    return
  }

  if (block.type === 'topic') return

  validateSectionContent(
    block.section.content,
    context,
    index,
    true,
    artigo.status === 'PUBLICADO',
    problems,
    tableIds
  )
}

function validateSectionContent(
  content: PortalArticleSectionContentItem[],
  context: string,
  blockIndex: number,
  isIntro: boolean,
  isPublished: boolean,
  problems: PortalArticleProblem[],
  tableIds: Set<string>
) {
  const validContentTypes: PortalArticleSectionContentItem['type'][] = [
    'paragraph',
    'bullets',
    'note',
    'cards',
    'image',
    'table',
    'youtube',
  ]

  content.forEach((item, itemIndex) => {
    const itemContext = `${context}: item ${itemIndex + 1}`
    const itemPath = `conteudo.${blockIndex}.section.content.${itemIndex}`

    if (!item?.type || !validContentTypes.includes(item.type)) {
      problems.push(createBlockProblem(`${itemContext} possui tipo inválido.`, blockIndex, 'section.content.type', `${itemPath}.type`))
      return
    }

    if (!['paragraph', 'image', 'youtube'].includes(item.type) && isIntro) {
      problems.push(createBlockProblem(`${context}: introdução aceita apenas parágrafos, imagens e vídeos do YouTube.`, blockIndex, 'section.content.type', `${itemPath}.type`))
      return
    }

    if (item.type === 'paragraph') {
      validateRichText(item.content, `${itemContext} parágrafo`, problems, {
        targetType: 'block',
        blockIndex,
        field: 'section.content.paragraph',
        path: `${itemPath}.content`,
      })
      return
    }

    if (item.type === 'bullets') {
      ;(item.items || []).forEach((bullet, bulletIndex) => {
        validateRichText(bullet, `${itemContext}: marcador ${bulletIndex + 1}`, problems, {
          targetType: 'block',
          blockIndex,
          field: 'section.content.bullets',
          path: `${itemPath}.items.${bulletIndex}`,
        })
      })
      return
    }

    if (item.type === 'note') {
      validateRichText(item.content, `${itemContext}: nota`, problems, {
        targetType: 'block',
        blockIndex,
        field: 'section.content.note',
        path: `${itemPath}.content`,
      })
      validateNoteStyle(item.noteStyle, itemContext, blockIndex, problems)
      return
    }

    if (item.type === 'image') {
      if (!hasText(item.image.src)) {
        problems.push(createBlockProblem(`${itemContext}: informe a URL/path da imagem.`, blockIndex, 'section.content.image.src', `${itemPath}.image.src`))
      }
      if (!hasText(item.image.alt)) {
        problems.push(createBlockProblem(`${itemContext}: informe o texto alternativo da imagem.`, blockIndex, 'section.content.image.alt', `${itemPath}.image.alt`))
      }
      if (!(item.image.width > 0) || !(item.image.height > 0)) {
        problems.push(createBlockProblem(`${itemContext}: informe largura e altura válidas.`, blockIndex, 'section.content.image.dimensions', `${itemPath}.image`))
      }
      if (!isValidOptionalImageHref(item.image.href)) {
        problems.push(createBlockProblem(`${itemContext}: informe um link http(s) ou caminho interno iniciado por /.`, blockIndex, 'section.content.image.href', `${itemPath}.image.href`))
      }
      validateRichText(item.image.caption, `${itemContext}: legenda`, problems, {
        targetType: 'block', blockIndex, field: 'section.content.image.caption', path: `${itemPath}.image.caption`,
      })
      return
    }

    if (item.type === 'table') {
      validateTableItem(item, itemContext, blockIndex, itemPath, isPublished, problems, tableIds)
      return
    }

    if (item.type === 'youtube') {
      if (!extractYoutubeVideoId(item.videoId)) {
        problems.push(createBlockProblem(`${itemContext}: informe uma URL válida do YouTube ou o ID de um vídeo.`, blockIndex, 'section.content.youtube.videoId', `${itemPath}.videoId`))
      }
      return
    }

    validateCardItems(item.items || [], itemContext, blockIndex, `${itemPath}.items`, problems)
  })
}

function isValidOptionalImageHref(value?: string) {
  if (!hasText(value)) return true
  return /^https?:\/\//i.test(value.trim()) || /^\/(?!\/)/.test(value.trim())
}

function validateTableItem(
  table: PortalArticleTableItem,
  context: string,
  blockIndex: number,
  path: string,
  isPublished: boolean,
  problems: PortalArticleProblem[],
  tableIds: Set<string>
) {
  const addProblem = (message: string, field: string, fieldPath = path) =>
    problems.push(createBlockProblem(message, blockIndex, field, fieldPath))

  if (!hasText(table.id)) addProblem(`${context}: tabela sem ID técnico.`, 'section.content.table.id', `${path}.id`)
  else if (tableIds.has(table.id)) addProblem(`${context}: ID de tabela duplicado.`, 'section.content.table.id', `${path}.id`)
  else tableIds.add(table.id)

  if (typeof table.name !== 'string') {
    addProblem(`${context}: nome da tabela deve ser texto.`, 'section.content.table.name', `${path}.name`)
  } else if (isPublished && !hasText(table.name)) {
    addProblem(`${context}: informe o nome da tabela.`, 'section.content.table.name', `${path}.name`)
  }

  if (!Array.isArray(table.columns)) {
    addProblem(`${context}: colunas devem ser uma lista.`, 'section.content.table.columns', `${path}.columns`)
    return
  }
  if (table.columns.length > PORTAL_TABLE_MAX_COLUMNS) {
    addProblem(`${context}: use no máximo ${PORTAL_TABLE_MAX_COLUMNS} colunas.`, 'section.content.table.columns', `${path}.columns`)
  }
  if (isPublished && table.columns.length < 2) {
    addProblem(`${context}: adicione ao menos 2 colunas.`, 'section.content.table.columns', `${path}.columns`)
  }

  const columnIds = new Set<string>()
  table.columns.forEach((column, columnIndex) => {
    const columnPath = `${path}.columns.${columnIndex}`
    if (!hasText(column.id)) addProblem(`${context}: coluna ${columnIndex + 1} sem ID técnico.`, 'section.content.table.column.id', `${columnPath}.id`)
    else if (columnIds.has(column.id)) addProblem(`${context}: ID de coluna duplicado.`, 'section.content.table.column.id', `${columnPath}.id`)
    else columnIds.add(column.id)

    if (typeof column.header !== 'string' || (isPublished && !hasText(column.header))) {
      addProblem(`${context}: informe o cabeçalho da coluna ${columnIndex + 1}.`, 'section.content.table.column.header', `${columnPath}.header`)
    }
    if (!['default', 'strong', 'accent', 'badge'].includes(column.style)) {
      addProblem(`${context}: estilo da coluna ${columnIndex + 1} é inválido.`, 'section.content.table.column.style', `${columnPath}.style`)
    }
    if (!['left', 'center', 'right'].includes(column.align)) {
      addProblem(`${context}: alinhamento da coluna ${columnIndex + 1} é inválido.`, 'section.content.table.column.align', `${columnPath}.align`)
    }
    if (!['auto', 'narrow', 'medium', 'wide'].includes(column.width)) {
      addProblem(`${context}: largura da coluna ${columnIndex + 1} é inválida.`, 'section.content.table.column.width', `${columnPath}.width`)
    }
  })

  if (!Array.isArray(table.rows)) {
    addProblem(`${context}: linhas devem ser uma lista.`, 'section.content.table.rows', `${path}.rows`)
    return
  }
  if (table.rows.length > PORTAL_TABLE_MAX_ROWS) {
    addProblem(`${context}: use no máximo ${PORTAL_TABLE_MAX_ROWS} linhas.`, 'section.content.table.rows', `${path}.rows`)
  }
  if (isPublished && table.rows.length === 0) {
    addProblem(`${context}: adicione ao menos uma linha.`, 'section.content.table.rows', `${path}.rows`)
  }

  const rowIds = new Set<string>()
  let hasData = false
  table.rows.forEach((row, rowIndex) => {
    const rowPath = `${path}.rows.${rowIndex}`
    if (!hasText(row.id)) addProblem(`${context}: linha ${rowIndex + 1} sem ID técnico.`, 'section.content.table.row.id', `${rowPath}.id`)
    else if (rowIds.has(row.id)) addProblem(`${context}: ID de linha duplicado.`, 'section.content.table.row.id', `${rowPath}.id`)
    else rowIds.add(row.id)

    if (!row.cells || typeof row.cells !== 'object' || Array.isArray(row.cells)) {
      addProblem(`${context}: células da linha ${rowIndex + 1} são inválidas.`, 'section.content.table.row.cells', `${rowPath}.cells`)
      return
    }
    const cellKeys = Object.keys(row.cells)
    if (cellKeys.some((columnId) => !columnIds.has(columnId))) {
      addProblem(`${context}: linha ${rowIndex + 1} referencia coluna inexistente.`, 'section.content.table.row.cells', `${rowPath}.cells`)
    }
    table.columns.forEach((column) => {
      const cell = row.cells[column.id]
      if (typeof cell !== 'string') {
        addProblem(`${context}: células devem ser texto.`, 'section.content.table.row.cells', `${rowPath}.cells.${column.id}`)
      } else if (hasText(cell)) hasData = true
    })
  })

  if (table.export?.enabled != null && typeof table.export.enabled !== 'boolean') {
    addProblem(`${context}: configuração de exportação inválida.`, 'section.content.table.export.enabled', `${path}.export.enabled`)
  }
  if (table.export?.fileName != null && typeof table.export.fileName !== 'string') {
    addProblem(`${context}: nome do arquivo deve ser texto.`, 'section.content.table.export.fileName', `${path}.export.fileName`)
  }
  if (isPublished && !hasData) {
    addProblem(`${context}: preencha ao menos uma célula.`, 'section.content.table.rows', `${path}.rows`)
  }
}

function validateCardItems(
  items: PortalArticleCardItem[],
  context: string,
  blockIndex: number,
  path: string,
  problems: PortalArticleProblem[]
) {
  items.forEach((item, itemIndex) => {
    const itemPath = `${path}.${itemIndex}`

    if (!item.kind) {
      problems.push(createBlockProblem(`${context}: card ${itemIndex + 1} está sem tipo.`, blockIndex, 'cards.item.kind', `${itemPath}.kind`))
      return
    }

    if (item.kind === 'course' && (!item.courseId || Number(item.courseId) <= 0)) {
      problems.push(createBlockProblem(`${context}: selecione o curso do card ${itemIndex + 1}.`, blockIndex, 'cards.item.courseId', `${itemPath}.courseId`))
    }
    if (item.kind === 'leadMagnet' && !hasText(item.slug)) {
      problems.push(createBlockProblem(`${context}: selecione o lead magnet do card ${itemIndex + 1}.`, blockIndex, 'cards.item.slug', `${itemPath}.slug`))
    }
    if (item.kind === 'portalPost' && !hasText(item.slug)) {
      problems.push(createBlockProblem(`${context}: informe o slug do artigo do card ${itemIndex + 1}.`, blockIndex, 'cards.item.slug', `${itemPath}.slug`))
    }
    if (!['course', 'leadMagnet', 'portalPost'].includes(item.kind)) {
      problems.push(createBlockProblem(`${context}: tipo do card ${itemIndex + 1} é inválido.`, blockIndex, 'cards.item.kind', `${itemPath}.kind`))
    }
  })
}

function validateNoteStyle(
  noteStyle: PortalArticleNoteStyle | undefined,
  context: string,
  blockIndex: number,
  problems: PortalArticleProblem[]
) {
  if (!noteStyle) return

  if (noteStyle.variant && !PORTAL_NOTE_STYLE_VARIANTS.includes(noteStyle.variant)) {
    problems.push(createBlockProblem(`${context}: estilo da nota inválido.`, blockIndex, 'section.noteStyle.variant', `conteudo.${blockIndex}.section.noteStyle.variant`))
  }

  PORTAL_NOTE_STYLE_COLOR_FIELDS.forEach(({ field, label }) => {
    const value = noteStyle[field]
    if (!hasText(value)) return
    if (PORTAL_HEX_COLOR_REGEX.test(value)) return

    problems.push(createBlockProblem(`${context}: cor de ${label} da nota deve ser HEX válido.`, blockIndex, `section.noteStyle.${field}`, `conteudo.${blockIndex}.section.noteStyle.${field}`))
  })
}

function validateSummaryHref(
  href: string,
  context: string,
  blockIndex: number,
  path: string,
  artigo: PortalArtigoModel,
  problems: PortalArticleProblem[]
) {
  const normalizedHref = normalizePortalAnchorValue(href)
  if (!normalizedHref) return
  if (hasPortalAnchor(artigo, normalizedHref)) return

  problems.push(createBlockProblem(`${context} aponta para uma âncora inexistente: "${href}".`, blockIndex, 'summary.href', path))
}

function validateDuplicateAnchors(artigo: PortalArtigoModel, problems: PortalArticleProblem[]) {
  const duplicates = findDuplicatePortalAnchors(artigo)

  duplicates.forEach((duplicate) => {
    problems.push(createProblem(`Anchor/ID duplicado: "${duplicate.value}" em ${duplicate.label}.`, 'article', 'anchor', 'anchors'))
  })
}

function validateFaq(artigo: PortalArtigoModel, problems: PortalArticleProblem[]) {
  const faq = artigo.faq
  if (!faq) return

  if ((faq.items || []).length === 0) {
    problems.push(createProblem('FAQ: adicione ao menos uma pergunta.', 'faq', 'items', 'faq.items'))
  }

  ;(faq.items || []).forEach((item, index) => {
    const context = `FAQ ${index + 1}`

    if (!hasText(item.question)) {
      problems.push(createProblem(`${context}: informe a pergunta.`, 'faq', 'question', `faq.items.${index}.question`))
    }

    if (!hasFaqAnswerContent(item.answer)) {
      problems.push(createProblem(`${context}: informe a resposta.`, 'faq', 'answer', `faq.items.${index}.answer`))
    }

    ;(item.answer || []).forEach((answerBlock, answerIndex) => {
      validateFaqAnswerBlock(answerBlock, context, problems, `faq.items.${index}.answer.${answerIndex}`)
    })
  })

  validateRichText(faq.whatsapp?.description, 'FAQ: descrição do WhatsApp', problems, {
    targetType: 'faq',
    field: 'whatsapp.description',
    path: 'faq.whatsapp.description',
  })
}

function validateFaqAnswerBlock(
  answerBlock: PortalFaqAnswerBlock,
  context: string,
  problems: PortalArticleProblem[],
  path: string
) {
  if (answerBlock.type === 'paragraph') {
    validateRichText(answerBlock.content, context, problems, {
      targetType: 'faq',
      field: 'answer',
      path,
    })
    return
  }

  ;(answerBlock.items || []).forEach((item, index) => {
    validateRichText(item, `${context}: item ${index + 1}`, problems, {
      targetType: 'faq',
      field: 'answer.items',
      path: `${path}.items.${index}`,
    })
  })
}

function validateCta(artigo: PortalArtigoModel, problems: PortalArticleProblem[]) {
  const cta = artigo.cta
  if (!cta) return

  if (!hasText(cta.title)) {
    problems.push(createProblem('CTA: informe o título.', 'cta', 'title', 'cta.title'))
  }
  if (!(cta.paragraphs || []).some(hasRichTextContent)) {
    problems.push(createProblem('CTA: adicione ao menos um parágrafo.', 'cta', 'paragraphs', 'cta.paragraphs'))
  }

  if (!hasText(cta.label)) {
    problems.push(createProblem('CTA: informe o texto do botão.', 'cta', 'label', 'cta.label'))
  }

  if (!hasText(cta.href)) {
    problems.push(createProblem('CTA: informe a URL do botão.', 'cta', 'href', 'cta.href'))
  } else if (!isSafeNavigationHref(cta.href)) {
    problems.push(createProblem('CTA: informe uma URL válida para o botão.', 'cta', 'href', 'cta.href'))
  }

  ;(cta.paragraphs || []).forEach((paragraph, index) => {
    validateRichText(paragraph, `CTA: parágrafo ${index + 1}`, problems, {
      targetType: 'cta',
      field: 'paragraphs',
      path: `cta.paragraphs.${index}`,
    })
  })
}

function validateCourses(artigo: PortalArtigoModel, problems: PortalArticleProblem[]) {
  const courses = artigo.cursosRelacionados
  if (!courses) return
  if ((courses.courseIds || []).length === 0) {
    problems.push(createProblem('Cursos relacionados: selecione ao menos um curso.', 'courses', 'courseIds', 'cursosRelacionados.courseIds'))
  }
}

function validateRelatedArticles(
  artigo: PortalArtigoModel,
  problems: PortalArticleProblem[]
) {
  const relatedArticles = artigo.artigosRelacionados
  if (!relatedArticles) return

  if (!hasText(relatedArticles.title)) {
    problems.push(
      createProblem(
        'Artigos relacionados: informe o título.',
        'relatedArticles',
        'title',
        'artigosRelacionados.title'
      )
    )
  }
  if (!hasText(relatedArticles.anchorId)) {
    problems.push(
      createProblem(
        'Artigos relacionados: informe a âncora.',
        'relatedArticles',
        'anchorId',
        'artigosRelacionados.anchorId'
      )
    )
  }

  const slugs = (relatedArticles.articleSlugs || []).map((slug) => slug.trim())
  if (slugs.length === 0) {
    problems.push(
      createProblem(
        'Artigos relacionados: selecione ao menos um artigo.',
        'relatedArticles',
        'articleSlugs',
        'artigosRelacionados.articleSlugs'
      )
    )
    return
  }
  if (slugs.some((slug) => !slug)) {
    problems.push(
      createProblem(
        'Artigos relacionados: remova referências sem slug.',
        'relatedArticles',
        'articleSlugs',
        'artigosRelacionados.articleSlugs'
      )
    )
  }
  if (new Set(slugs).size !== slugs.length) {
    problems.push(
      createProblem(
        'Artigos relacionados: remova artigos duplicados.',
        'relatedArticles',
        'articleSlugs',
        'artigosRelacionados.articleSlugs'
      )
    )
  }
  if (slugs.includes(artigo.slug.trim())) {
    problems.push(
      createProblem(
        'Artigos relacionados: o artigo atual não pode relacionar a si mesmo.',
        'relatedArticles',
        'articleSlugs',
        'artigosRelacionados.articleSlugs'
      )
    )
  }
}

function validateRichText(
  value: PortalRichText | undefined,
  context: string,
  problems: PortalArticleProblem[],
  target: Pick<PortalArticleProblem, 'targetType' | 'blockIndex' | 'field' | 'path'>
) {
  if (!value) return

  const parts = Array.isArray(value) ? value : [value]
  parts.forEach((part) => validateRichTextPart(part, context, problems, target))
}

function validateRichTextPart(
  part: PortalRichTextPart,
  context: string,
  problems: PortalArticleProblem[],
  target: Pick<PortalArticleProblem, 'targetType' | 'blockIndex' | 'field' | 'path'>
) {
  if (typeof part === 'string') return
  if (part.type !== 'link') return

  if (!hasText(part.text)) {
    problems.push(createProblem(`${context}: link sem texto.`, target.targetType, target.field, target.path, target.blockIndex))
  }
  if (!hasText(part.href)) {
    problems.push(createProblem(`${context}: link sem URL.`, target.targetType, target.field, target.path, target.blockIndex))
  } else if (!isSafeNavigationHref(part.href)) {
    problems.push(createProblem(`${context}: link com URL inválida.`, target.targetType, target.field, target.path, target.blockIndex))
  }
}

function hasFaqAnswerContent(answerBlocks: PortalFaqAnswerBlock[] = []) {
  return answerBlocks.some((answerBlock) => {
    if (answerBlock.type === 'paragraph') return hasRichTextContent(answerBlock.content)
    return (answerBlock.items || []).some(hasRichTextContent)
  })
}

function hasRichTextContent(value?: PortalRichText) {
  if (!value) return false

  const parts = Array.isArray(value) ? value : [value]
  return parts.some((part) => {
    if (typeof part === 'string') return hasText(part)
    if (hasText(part.text)) return true
    return part.type === 'link' ? hasText(part.href) : false
  })
}

function createBlockProblem(
  message: string,
  blockIndex: number,
  field?: string,
  path?: string
): PortalArticleProblem {
  return createProblem(message, 'block', field, path, blockIndex)
}

function createTopicProblem(
  message: string,
  blockIndex: number,
  topicIndex: number,
  topicItemIndex?: number,
  field?: string,
  path?: string
): PortalArticleProblem {
  return {
    ...createBlockProblem(message, blockIndex, field, path),
    topicIndex,
    topicItemIndex,
  }
}

function createProblem(
  message: string,
  targetType: PortalArticleProblemTargetType,
  field?: string,
  path?: string,
  blockIndex?: number
): PortalArticleProblem {
  return {
    message,
    severity: 'erro',
    targetType,
    blockIndex,
    field,
    path,
  }
}

function hasText(value?: string | null) {
  return typeof value === 'string' && value.trim().length > 0
}

function hasPartialPair(first?: string | null, second?: string | null) {
  return (hasText(first) && !hasText(second)) || (!hasText(first) && hasText(second))
}

function isSafeNavigationHref(value: string) {
  const href = value.trim()
  if (href.startsWith('//')) return false
  if (href.startsWith('/') || href.startsWith('#')) return true
  if (/^(https?:|mailto:|tel:)/i.test(href)) return true
  return !/^[a-z][a-z\d+.-]*:/i.test(href)
}
